import React, { useRef } from 'react';

import { DeselectedResource } from '../../actions/menuActions';
import { AudioCaptionsContextProvider } from '../../components/Audio/AudioCaptionsContextProvider';
import { StudentAudioAssetPlayer } from '../../components/Audio/editorComponents/StudentAudioAssetPlayer';
import { ContentWrapper } from '../../components/ContentWrapper/ContentWrapper';
import { CloseNavigationButton } from '../../components/NavigationButtons';
import { ContentBlocksRenderer } from '../../ContentZone/student/ContentBlocksRenderer';
import { useIsMobileV2 } from '../../hooks/useIsMobile';
import { getItemByIdTypedSelector } from '../../store/reducers/items/itemsSelectors';
import { useStudentSelector } from '../../store/selectors/useStudentSelector';
import { isResource } from '@ael/core-api';
import { StudentNavigationBar } from '@ael/shared-components-library';
import { useDispatch } from 'react-redux';
import { FroalaEditorsSettings } from 'src/config/configs';

interface ResourceContentProps {
  resourceId: string;
  froalaEditorsSettings: FroalaEditorsSettings;
}

export const ResourceContent: React.FC<ResourceContentProps> = ({ resourceId, froalaEditorsSettings }) => {
  const dispatch = useDispatch();
  const resource = useStudentSelector(getItemByIdTypedSelector)(resourceId, isResource);

  const isMobile = useIsMobileV2();
  const onClose = () => dispatch(DeselectedResource(isMobile));

  const toolbarRef = useRef(null);

  return (
    <AudioCaptionsContextProvider>
      {!isMobile ? (
        <ContentWrapper>
          <ContentWrapper.Modal>
            <ContentWrapper.DesktopContent key={resource.id}>
              <ContentWrapper.Title title={resource.title} />
              <ContentWrapper.Body noHorizontalPadding>
                <ContentBlocksRenderer
                  contentItem={resource}
                  content={resource.content}
                  key={resource.id}
                  inSmallWindow={false}
                  froalaEditorsSettings={froalaEditorsSettings}
                />
              </ContentWrapper.Body>
            </ContentWrapper.DesktopContent>
            <ContentWrapper.Toolbar ref={toolbarRef}>
              <StudentNavigationBar ref={toolbarRef} type="StudentFull">
                <StudentNavigationBar.ButtonBox>
                  {resource.audioAsset && (
                    <StudentAudioAssetPlayer
                      uniqueAudioKey={resource.id}
                      audioAsset={resource.audioAsset}
                      isMobileDevice={false}
                    />
                  )}
                </StudentNavigationBar.ButtonBox>
                <StudentNavigationBar.ButtonBox justifyEnd hasLabels>
                  <CloseNavigationButton onClick={onClose} big showLabel />
                </StudentNavigationBar.ButtonBox>
              </StudentNavigationBar>
            </ContentWrapper.Toolbar>
          </ContentWrapper.Modal>
          <ContentWrapper.Caption />
        </ContentWrapper>
      ) : (
        <ContentWrapper>
          <ContentWrapper.Modal>
            <ContentWrapper.MobileContent key={resource.id}>
              <ContentWrapper.Title title={resource.title} small />
              <ContentWrapper.Body noHorizontalPadding>
                <ContentBlocksRenderer
                  contentItem={resource}
                  content={resource.content}
                  key={resource.id}
                  inSmallWindow={false}
                  froalaEditorsSettings={froalaEditorsSettings}
                />
              </ContentWrapper.Body>
            </ContentWrapper.MobileContent>
          </ContentWrapper.Modal>
          <ContentWrapper.Toolbar ref={toolbarRef}>
            <StudentNavigationBar ref={toolbarRef} type="StudentFull">
              {resource.audioAsset && (
                <StudentNavigationBar.ButtonBox>
                  <StudentAudioAssetPlayer
                    uniqueAudioKey={resource.id}
                    audioAsset={resource.audioAsset}
                    isMobileDevice={true}
                  />
                </StudentNavigationBar.ButtonBox>
              )}
              <StudentNavigationBar.ButtonBox justifyEnd>
                <CloseNavigationButton onClick={onClose} />
              </StudentNavigationBar.ButtonBox>
            </StudentNavigationBar>
          </ContentWrapper.Toolbar>
          <ContentWrapper.Caption />
        </ContentWrapper>
      )}
    </AudioCaptionsContextProvider>
  );
};
